import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import { isEkycVerified, getHtxDashboard, getDebtsByHtx, getHtxAverageRiskScore } from '@/store';
import { formatCurrencyCompact } from '@/utils/formatters';
import { getKycColor, getKycLabel } from '@/utils/status';
import { BottomSheet } from '@/components/base/BottomSheet';
import { ActionIconGrid } from '@/components/base/ActionIconGrid';
import { CompactAlert } from '@/components/base/CompactAlert';
import { ImportantMetric } from '@/components/base/ImportantMetric';
import { HomeFinanceQuickSheets } from './components/HomeFinanceQuickSheets';
import type { HomeFinanceAction } from './components/HomeFinanceQuickSheets';

/** Duyệt vay/Cấp hạn mức/Giải ngân/Quỹ HTX quick actions were removed from this
 * dashboard — they're Agrida-internal actions with no accessible endpoint for
 * HTX_STAFF in the real backend (see task 27 notes). This page now shows real
 * aggregate counts from the portal dashboard endpoint, and keeps "Xem công nợ" as
 * the one real, HTX-accessible money action (confirming farmer repayments). */
export default function HtxOverviewPage() {
  useStore();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeFinanceAction, setActiveFinanceAction] = useState<HomeFinanceAction | null>(null);

  if (!user || user.role !== 'Hợp tác xã') {
    return <Navigate to="/dang-nhap" replace />;
  }

  const htxEkycVerified = isEkycVerified(user);
  const dashboard = getHtxDashboard(user.htxId);
  const overdueCount = getDebtsByHtx(user.htxId).filter((d) => d.status === 'Quá hạn').length;
  const avgRisk = getHtxAverageRiskScore(user.htxId);

  const kycLabel = getKycLabel(user.kycStatus || 'registered');
  const kycColorMap = getKycColor(user.kycStatus || 'registered');

  const today = new Date();
  const syncDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

  return (
    <div className="px-3.5 sm:px-5 py-4 pb-5 space-y-5 max-w-[1200px] mx-auto">
      {/* ========== HEADER ========== */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">HTX Dashboard</p>
          <h2 className="text-lg font-bold text-gray-900 leading-tight line-clamp-2 text-safe-normal">{user.displayName}</h2>
        </div>
        <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ml-2 ${kycColorMap.bg} ${kycColorMap.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${user.kycStatus === 'ekyc_verified' ? 'bg-agrida-500' : 'bg-amber-500'}`} />
          {kycLabel}
        </span>
      </div>

      {!htxEkycVerified && (
        <CompactAlert
          tone="warning"
          title="Xác thực eKYC để mở khóa chức năng tài chính"
          description="Tài khoản HTX của bạn chưa được xác thực. Bạn vẫn có thể xem danh sách, hợp đồng và báo cáo."
        />
      )}

      {/* ========== NHÓM TIỀN ========== */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-5 rounded-full bg-agrida-500" />
            <h3 className="text-sm font-semibold text-gray-900">Tiền</h3>
          </div>
        </div>
        <ActionIconGrid
          columns={3}
          actions={[
            {
              id: 'loans',
              icon: 'ri-file-list-3-line',
              label: 'Khoản vay nông hộ',
              bgColor: 'bg-agrida-50',
              iconColor: 'text-agrida-600',
              badgeCount: dashboard?.loans.pendingApprovalCount || 0,
              onClick: () => navigate('/htx/vay-han-muc'),
            },
            {
              id: 'debts',
              icon: 'ri-money-cny-circle-line',
              label: 'Xem công nợ',
              bgColor: 'bg-indigo-50',
              iconColor: 'text-indigo-600',
              badgeCount: overdueCount,
              onClick: () => setActiveFinanceAction('debts'),
            },
            {
              id: 'contracts',
              icon: 'ri-file-text-line',
              label: 'Hợp đồng',
              bgColor: 'bg-teal-50',
              iconColor: 'text-teal-600',
              badgeCount: dashboard?.contracts.expiringSoonCount || 0,
              onClick: () => navigate('/htx/hop-dong'),
            },
          ]}
        />
      </div>

      {/* ========== TỔNG QUAN HTX ========== */}
      <div className="mobile-card p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <i className="ri-group-line text-teal-600 text-base" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Tổng quan hợp tác xã</p>
            <p className="text-[10px] text-gray-400">Số liệu cập nhật theo thời gian thực</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <ImportantMetric label="Nông hộ" value={`${dashboard?.farmers.total ?? 0} hộ`} tone="slate" size="sm" />
          <ImportantMetric label="Khoản vay đang hoạt động" value={dashboard?.loans.activeCount ?? 0} tone="emerald" size="sm" />
          <ImportantMetric label="Dư nợ" value={formatCurrencyCompact(Number(dashboard?.loans.totalOutstandingVnd || 0))} tone="amber" size="sm" />
          <ImportantMetric
            label="Điểm rủi ro TB (5C)"
            value={avgRisk?.averageScore !== undefined && avgRisk.averageScore !== null ? `${avgRisk.averageScore.toFixed(0)}/100` : 'Chưa có'}
            tone="slate"
            size="sm"
          />
        </div>
        {avgRisk && avgRisk.totalCount > 0 && (
          <p className="text-[10px] text-gray-400 mt-2">
            {avgRisk.scoredCount}/{avgRisk.totalCount} nông hộ đã có điểm chấm — trung bình chỉ tính trên số đã chấm.
          </p>
        )}
      </div>

      {/* ========== eKYC TÀI KHOẢN HTX ========== */}
      {htxEkycVerified ? (
        <CompactAlert
          tone="success"
          title="Tài khoản HTX đã xác thực eKYC"
          description="Danh tính hợp tác xã đã được xác minh thành công"
          compact
        />
      ) : (
        <button type="button" onClick={() => navigate('/ekyc')} className="w-full">
          <CompactAlert
            tone="warning"
            title="Tài khoản HTX chưa xác thực eKYC"
            description="Nhấn vào đây để thực hiện xác thực danh tính hợp tác xã"
            compact
          />
        </button>
      )}

      <p className="text-[10px] text-gray-300 text-center">Đồng bộ: {syncDate}</p>

      <HomeFinanceQuickSheets action={activeFinanceAction} onClose={() => setActiveFinanceAction(null)} />
    </div>
  );
}
